/**
 * @file TailwindCSS container queries plugin with support for max width container queries
 * @see https://github.com/tailwindlabs/tailwindcss-container-queries
 */
import plugin from 'tailwindcss/plugin'
import type { PluginAPI } from 'tailwindcss/types/config'

const parseValue = (value: string): number | null => {
  const numericValue = value.match(/^(\d+\.\d+|\d+|\.\d+)\D+/)?.[1] ?? null
  if (numericValue === null) return null
  return parseFloat(value)
}

const sort: (a: { value: string; modifier: string | null }, b: { value: string; modifier: string | null }) => number = (
  aVariant,
  zVariant,
) => {
  const a = parseFloat(aVariant.value)
  const z = parseFloat(zVariant.value)

  if (a === null || z === null) return 0

  // Sort values themselves regardless of unit
  if (a - z !== 0) return a - z

  const aLabel = aVariant.modifier ?? ''
  const zLabel = zVariant.modifier ?? ''

  // Explicitly move empty labels to the end
  if (aLabel === '' && zLabel !== '') {
    return 1
  } else if (aLabel !== '' && zLabel === '') {
    return -1
  }

  // Sort labels alphabetically in the English locale
  // We are intentionally overriding the locale because we do not want the sort to
  // be affected by the machine's locale (be it a developer or CI environment)
  return aLabel.localeCompare(zLabel, 'en', { numeric: true })
}

const containerQueriesPlugin = plugin(
  function containerQueries({ matchUtilities, matchVariant, addVariant, theme }: PluginAPI) {
    const values: Record<string, string> = theme('containers') ?? {}

    /**
     * Register @container utility class
     */
    matchUtilities(
      {
        '@container': (value, { modifier }) => {
          return {
            'container-type': value,
            'container-name': modifier,
          }
        },
      },
      {
        values: {
          DEFAULT: 'inline-size',
          normal: 'normal',
        },
        modifiers: 'any',
      },
    )

    /**
     * Register @ variant
     */
    matchVariant(
      '@',
      (value = '', { modifier }) => {
        const parsed = parseValue(value)
        return parsed !== null ? `@container ${modifier ?? ''} (min-width: ${value})` : []
      },
      {
        values,
        sort,
      },
    )

    /**
     * Register @max variant
     * @see https://github.com/tailwindlabs/tailwindcss-container-queries/pull/20
     */
    const maxVariantFn: (value: string, { modifier }: { modifier: string | null }) => string | string[] = (
      value = '',
      { modifier },
    ) => {
      const parsed = parseValue(value)
      return parsed !== null ? `@container ${modifier ?? ''} (max-width: ${value})` : []
    }

    matchVariant('@max', maxVariantFn, {
      values,
      sort,
    })

    /**
     * Register -@ variant
     * Use this to match elements that don't have a container ancestor.
     * This variant must be put last.
     * @example hhover:-@:m-0
     *
     * How it works: Tailwind substitutes `&` with the utility's own selector, so
     * `-@:m-0` compiles to `.-\@\:m-0:not([class*="@container"] *)`. The argument to
     * `:not()` is an ancestry filter on that subject element — `[class*="@container"] *`
     * matches any element nested under something carrying an `@container` class, so the
     * `:not()` keeps only subjects that have no container ancestor. (`*` is equivalent
     * to repeating the utility's own class here, since the subject is already pinned by
     * the leading `&`; it just avoids depending on `&` being substituted inside `:not()`.)
     */
    addVariant('-@', '&:not([class*="@container"] *)')
  },
  {
    theme: {
      containers: {},
    },
  },
)

export { containerQueriesPlugin }
